import { Plus, Trash2, UserCheck } from 'lucide-react-native';
import { useState } from 'react';
import { ActivityIndicator, Alert, Platform, StyleSheet } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Divider } from '../../../components/primitives/Divider';
import { Input } from '../../../components/primitives/Input';
import { Select } from '../../../components/primitives/Select';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { type TranslationParams, useTranslation } from '../../../contexts/TranslationContext';
import { useCreateIndustry, useDeleteIndustry, useIndustries } from '../../../hooks/useIndustries';
import {
  useCreateSellerProfile,
  useDeleteSellerProfile,
  useSellerProfiles,
  useUpdateSellerProfile,
} from '../../../hooks/useSellerProfiles';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { Industry, SellerStatus, SellerType } from '../../../types/leads';
import { SELLER_STATUS_LABELS, SELLER_TYPE_LABELS } from '../../../types/leads';
import { useMembers } from '../hooks/useSettings';
import type { MemberData } from '../types';
import { SettingsSection } from './SettingsSection';

const SELLER_TYPE_OPTIONS = Object.entries(SELLER_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const SELLER_STATUS_OPTIONS = Object.entries(SELLER_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const STATUS_BADGE_COLOR: Record<SellerStatus, 'cyan' | 'neutral' | 'amber'> = {
  active: 'cyan',
  inactive: 'neutral',
  on_leave: 'amber',
};

function getInitials(fullName: string): string {
  if (!fullName) return '?';
  return fullName.charAt(0).toUpperCase();
}

function confirmDeleteProfile(
  memberName: string,
  onConfirm: () => void,
  t: (key: string, params?: TranslationParams) => string,
) {
  const message = t('settings.sellerProfiles.deleteConfirmMessage', { name: memberName });
  if (Platform.OS === 'web') {
    const confirmed = globalThis.confirm(message);
    if (confirmed) onConfirm();
  } else {
    Alert.alert(t('settings.sellerProfiles.deleteConfirmTitle'), message, [
      { text: t('common.actions.cancel'), style: 'cancel' },
      { text: t('common.actions.delete'), style: 'destructive', onPress: onConfirm },
    ]);
  }
}

function confirmDeleteIndustry(
  onConfirm: () => void,
  t: (key: string, params?: TranslationParams) => string,
) {
  const message = t('settings.sellerProfiles.deleteIndustryMessage');
  if (Platform.OS === 'web') {
    const confirmed = globalThis.confirm(message);
    if (confirmed) onConfirm();
  } else {
    Alert.alert(t('settings.sellerProfiles.deleteIndustryTitle'), message, [
      { text: t('common.actions.cancel'), style: 'cancel' },
      { text: t('common.actions.delete'), style: 'destructive', onPress: onConfirm },
    ]);
  }
}

interface ProfileFormState {
  sellerType: SellerType;
  status: SellerStatus;
  maxLeads: string;
  bio: string;
  industryIds: string[];
}

const DEFAULT_FORM: ProfileFormState = {
  sellerType: 'sdr',
  status: 'active',
  maxLeads: '',
  bio: '',
  industryIds: [],
};

interface MemberRowProps {
  member: MemberData;
  existingProfile: {
    sellerType: SellerType;
    status: SellerStatus;
    maxLeads: number | null;
    bio: string | null;
    industries: Industry[];
  } | null;
  canManage: boolean;
  isExpanded: boolean;
  onToggleExpand: (userId: string | null) => void;
  allIndustries: Industry[];
  t: (key: string, params?: TranslationParams) => string;
}

function MemberRow({
  member,
  existingProfile,
  canManage,
  isExpanded,
  onToggleExpand,
  allIndustries,
  t,
}: Readonly<MemberRowProps>) {
  const createProfile = useCreateSellerProfile();
  const updateProfile = useUpdateSellerProfile();
  const deleteProfile = useDeleteSellerProfile();

  const [form, setForm] = useState<ProfileFormState>(() => {
    if (existingProfile) {
      return {
        sellerType: existingProfile.sellerType,
        status: existingProfile.status,
        maxLeads: existingProfile.maxLeads != null ? String(existingProfile.maxLeads) : '',
        bio: existingProfile.bio ?? '',
        industryIds: existingProfile.industries.map((i) => i.id),
      };
    }
    return DEFAULT_FORM;
  });

  const handleOpen = () => {
    if (existingProfile) {
      setForm({
        sellerType: existingProfile.sellerType,
        status: existingProfile.status,
        maxLeads: existingProfile.maxLeads != null ? String(existingProfile.maxLeads) : '',
        bio: existingProfile.bio ?? '',
        industryIds: existingProfile.industries.map((i) => i.id),
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    onToggleExpand(member.userId);
  };

  const handleCancel = () => onToggleExpand(null);

  const handleSave = () => {
    const payload = {
      sellerType: form.sellerType,
      status: form.status,
      maxLeads: form.maxLeads ? Number(form.maxLeads) : undefined,
      bio: form.bio.trim() || undefined,
      industryIds: form.industryIds,
    };

    const onSuccess = () => onToggleExpand(null);

    if (existingProfile) {
      updateProfile.mutate({ userId: member.userId, data: payload }, { onSuccess });
    } else {
      createProfile.mutate({ userId: member.userId, ...payload }, { onSuccess });
    }
  };

  const handleDelete = () => {
    confirmDeleteProfile(member.fullName, () => deleteProfile.mutate(member.userId), t);
  };

  const toggleIndustry = (id: string) => {
    setForm((prev) => ({
      ...prev,
      industryIds: prev.industryIds.includes(id)
        ? prev.industryIds.filter((i) => i !== id)
        : [...prev.industryIds, id],
    }));
  };

  const isSaving = createProfile.isPending || updateProfile.isPending;

  return (
    <VStack spacing="sm">
      <VStack spacing="sm" style={styles.memberRow}>
        <HStack spacing="md" align="center">
          <Box
            width={40}
            height={40}
            borderRadius="full"
            backgroundColor={colors.primary.muted}
            alignItems="center"
            justifyContent="center"
          >
            <Text variant="body" weight="semibold" color={colors.primary[500]}>
              {getInitials(member.fullName)}
            </Text>
          </Box>

          <VStack spacing="xxs" style={styles.memberInfo}>
            <Text
              variant="body"
              weight="medium"
              color={colors.text.primary}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {member.fullName}
            </Text>
            <Text
              variant="caption"
              color={colors.text.secondary}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {member.email}
            </Text>
          </VStack>
        </HStack>

        <HStack spacing="sm" align="center" justify="end" wrap>
          {existingProfile && (
            <Badge color={STATUS_BADGE_COLOR[existingProfile.status]} size="sm">
              {SELLER_STATUS_LABELS[existingProfile.status]}
            </Badge>
          )}
          {existingProfile && (
            <Badge color="blue" size="sm">
              {SELLER_TYPE_LABELS[existingProfile.sellerType]}
            </Badge>
          )}
          {!existingProfile && (
            <Badge color="neutral" size="sm">
              {t('settings.sellerProfiles.noProfile')}
            </Badge>
          )}
          {canManage && (
            <Box
              pressable
              onPress={handleOpen}
              padding="sm"
              borderRadius="md"
              backgroundColor={colors.primary.muted}
              accessibilityLabel={
                existingProfile
                  ? t('settings.sellerProfiles.editProfile')
                  : t('settings.sellerProfiles.createProfile')
              }
            >
              <UserCheck size={16} color={colors.primary[500]} />
            </Box>
          )}
          {canManage && existingProfile && (
            <Box
              pressable
              onPress={handleDelete}
              padding="sm"
              borderRadius="md"
              backgroundColor={colors.status.error.bg}
              accessibilityLabel={t('settings.sellerProfiles.deleteProfile')}
            >
              <Trash2 size={16} color={colors.status.error.main} />
            </Box>
          )}
        </HStack>
      </VStack>

      {isExpanded && (
        <Box
          backgroundColor={colors.background.tertiary}
          borderRadius="lg"
          padding="lg"
          borderWidth={1}
          borderColor={colors.border.muted}
        >
          <VStack spacing="md">
            <Text variant="body" weight="semibold" color={colors.text.primary}>
              {existingProfile
                ? t('settings.sellerProfiles.editProfile')
                : t('settings.sellerProfiles.createProfile')}
            </Text>

            <VStack spacing="xs" style={styles.selectFieldFirst}>
              <Text variant="bodySmall" color={colors.text.primary} style={styles.fieldLabel}>
                {t('settings.sellerProfiles.sellerType')}
              </Text>
              <Select
                variant="dropdown"
                options={SELLER_TYPE_OPTIONS}
                value={form.sellerType}
                onChange={(v) => setForm((prev) => ({ ...prev, sellerType: v as SellerType }))}
              />
            </VStack>

            <VStack spacing="xs" style={styles.selectFieldSecond}>
              <Text variant="bodySmall" color={colors.text.primary} style={styles.fieldLabel}>
                {t('settings.sellerProfiles.status')}
              </Text>
              <Select
                variant="dropdown"
                options={SELLER_STATUS_OPTIONS}
                value={form.status}
                onChange={(v) => setForm((prev) => ({ ...prev, status: v as SellerStatus }))}
              />
            </VStack>

            <Input
              label={t('settings.sellerProfiles.maxLeads')}
              placeholder={t('settings.sellerProfiles.maxLeadsPlaceholder')}
              value={form.maxLeads}
              onChangeText={(v) =>
                setForm((prev) => ({ ...prev, maxLeads: v.replace(/[^0-9]/g, '') }))
              }
              keyboardType="numeric"
            />

            <Input
              label={t('settings.sellerProfiles.bio')}
              placeholder={t('settings.sellerProfiles.bioPlaceholder')}
              value={form.bio}
              onChangeText={(v) => setForm((prev) => ({ ...prev, bio: v }))}
              multiline
              numberOfLines={3}
            />

            {allIndustries.length > 0 && (
              <VStack spacing="xs">
                <Text variant="bodySmall" color={colors.text.primary} style={styles.fieldLabel}>
                  {t('settings.sellerProfiles.industries')}
                </Text>
                <HStack spacing="xs" style={styles.industryChips}>
                  {allIndustries.map((industry) => {
                    const selected = form.industryIds.includes(industry.id);
                    return (
                      <Box
                        key={industry.id}
                        pressable
                        onPress={() => toggleIndustry(industry.id)}
                        padding="sm"
                        borderRadius="full"
                        backgroundColor={
                          selected ? colors.primary.muted : colors.background.tertiary
                        }
                        borderWidth={1}
                        borderColor={selected ? colors.primary[500] : colors.border.muted}
                        accessibilityLabel={industry.name}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: selected }}
                      >
                        <Text
                          variant="caption"
                          color={selected ? colors.primary[500] : colors.text.secondary}
                          weight={selected ? 'semibold' : 'normal'}
                        >
                          {industry.name}
                        </Text>
                      </Box>
                    );
                  })}
                </HStack>
              </VStack>
            )}

            {(createProfile.isError || updateProfile.isError) && (
              <Text variant="caption" color={colors.status.error.main}>
                {t('settings.sellerProfiles.saveError')}
              </Text>
            )}

            <HStack spacing="sm" justify="end">
              <Button variant="ghost" size="sm" onPress={handleCancel} disabled={isSaving}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                {t('settings.sellerProfiles.saveProfile')}
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}
    </VStack>
  );
}

function IndustriesSubSection({
  orgId,
  canManage,
  t,
}: Readonly<{
  orgId: string;
  canManage: boolean;
  t: (key: string, params?: TranslationParams) => string;
}>) {
  const { data: industries, isLoading } = useIndustries(orgId);
  const createIndustry = useCreateIndustry();
  const deleteIndustry = useDeleteIndustry();
  const [newName, setNewName] = useState('');

  const handleAdd = () => {
    if (!newName.trim()) return;
    createIndustry.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') });
  };

  return (
    <VStack spacing="md">
      <Text variant="body" weight="medium" color={colors.text.primary}>
        {t('settings.sellerProfiles.industriesTitle')}
      </Text>

      {isLoading ? (
        <ActivityIndicator size="small" color={colors.primary[400]} />
      ) : (
        <VStack spacing="sm">
          {industries?.map((industry) => (
            <HStack key={industry.id} spacing="sm" align="center" style={styles.industryRow}>
              <Text variant="body" color={colors.text.primary} style={styles.industryName}>
                {industry.name}
              </Text>
              {industry.isDefault && (
                <Badge color="neutral" size="sm">
                  default
                </Badge>
              )}
              {canManage && !industry.isDefault && (
                <Box
                  pressable
                  onPress={() => confirmDeleteIndustry(() => deleteIndustry.mutate(industry.id), t)}
                  padding="xs"
                  borderRadius="md"
                  backgroundColor={colors.status.error.bg}
                  accessibilityLabel={`Delete ${industry.name}`}
                >
                  <Trash2 size={14} color={colors.status.error.main} />
                </Box>
              )}
            </HStack>
          ))}
          {(!industries || industries.length === 0) && (
            <Text variant="body" color={colors.text.secondary}>
              {t('settings.sellerProfiles.noIndustriesList')}
            </Text>
          )}
        </VStack>
      )}

      {canManage && (
        <VStack spacing="sm">
          <HStack spacing="sm" align="end">
            <Box style={styles.inputFlex}>
              <Input
                placeholder={t('settings.sellerProfiles.industryPlaceholder')}
                value={newName}
                onChangeText={setNewName}
                onSubmitEditing={handleAdd}
              />
            </Box>
            <Button
              variant="primary"
              size="sm"
              onPress={handleAdd}
              loading={createIndustry.isPending}
              disabled={!newName.trim()}
            >
              <HStack spacing="xs" align="center">
                <Plus size={16} color={colors.background.primary} />
                <Text variant="bodySmall" weight="medium" color={colors.background.primary}>
                  {t('settings.sellerProfiles.addIndustry')}
                </Text>
              </HStack>
            </Button>
          </HStack>
          {createIndustry.isError && (
            <Text variant="caption" color={colors.status.error.main}>
              {t('settings.sellerProfiles.addIndustryError')}
            </Text>
          )}
        </VStack>
      )}
    </VStack>
  );
}

export function SellerProfilesSection() {
  const { backendUser, currentOrganization } = useAuth();
  const { t } = useTranslation();

  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    role: string;
  }> = backendUser?.organizationMembers ?? backendUser?.organizations ?? [];

  const orgMembership = memberships.find(
    (o) => (o.organization?.id ?? o.organizationId) === currentOrganization?.id,
  );
  const currentRole = orgMembership?.role ?? 'sales_rep';
  const canManage = currentRole === 'owner' || currentRole === 'admin';

  const orgId = currentOrganization?.id;
  const { data: membersResponse, isLoading: membersLoading } = useMembers(orgId);
  const { data: profiles, isLoading: profilesLoading } = useSellerProfiles(orgId);
  const { data: industries } = useIndustries(orgId);

  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

  const members = membersResponse?.data ?? [];
  const isLoading = membersLoading || profilesLoading;

  const profilesByUserId = new Map(profiles?.map((p) => [p.userId, p]) ?? []);

  if (isLoading) {
    return (
      <SettingsSection title={t('settings.sellerProfiles.title')}>
        <Box alignItems="center" justifyContent="center" minHeight={120}>
          <ActivityIndicator size="large" color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.text.secondary} style={styles.loadingText}>
            {t('settings.sellerProfiles.loading')}
          </Text>
        </Box>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection title={t('settings.sellerProfiles.title')}>
      <VStack spacing="sm">
        {members.map((member) => {
          const profile = profilesByUserId.get(member.userId) ?? null;
          return (
            <MemberRow
              key={member.userId}
              member={member}
              existingProfile={profile}
              canManage={canManage}
              isExpanded={expandedUserId === member.userId}
              onToggleExpand={(id) => setExpandedUserId((prev) => (prev === id ? null : id))}
              allIndustries={industries ?? []}
              t={t}
            />
          );
        })}
        {members.length === 0 && (
          <Text variant="body" color={colors.text.secondary}>
            {t('settings.sellerProfiles.noMembers')}
          </Text>
        )}
      </VStack>

      <Divider color={colors.border.subtle} marginY="sm" />

      <IndustriesSubSection orgId={orgId!} canManage={canManage} t={t} />
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  memberRow: {
    paddingVertical: tokens.spacing.sm,
  },
  memberInfo: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabel: {
    fontWeight: '500',
  },
  selectFieldFirst: {
    zIndex: 20,
  },
  selectFieldSecond: {
    zIndex: 10,
  },
  industryChips: {
    flexWrap: 'wrap',
  },
  industryRow: {
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.background.tertiary,
  },
  industryName: {
    flex: 1,
  },
  inputFlex: {
    flex: 1,
  },
  loadingText: {
    marginTop: tokens.spacing.md,
  },
});
